import { useEffect, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import hash from 'object-hash';

import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { ToolkitId } from '@e-socna/data-layer';

import { CheckboxList } from '../../../components/CheckboxList';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../../hooks/useFormikOnNextTick.hook';

import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

interface Props {
  readonly id: string;
}

export const ResolvableMissionTypeToolkitsFormStep = ({}: Readonly<Props>) => {
  const { errors, setFieldTouched, touched, values } =
    useFormikContext<ResolvableMissionTypeForm_ResolvableMissionType>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const { toolkits } = useReferencesContext();

  const enabledToolkitIds = getIn(values, 'enabledToolkitIds') as
    readonly ToolkitId[] | null | undefined;

  const enabledToolkitCheckboxIdPrefix = `${hash(`${values._id}-enabledToolkitIds`)}-`;

  const [checkedEnabledToolkitIds, setCheckedEnabledToolkitIds] =
    useState(enabledToolkitIds);

  useEffect(() => {
    setFieldValueOnNextTick('enabledToolkitIds', checkedEnabledToolkitIds);
  }, [checkedEnabledToolkitIds, setFieldValueOnNextTick]);

  return (
    <Grid container spacing={2}>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <FormControl
          component="fieldset"
          error={!!(errors.enabledToolkitIds && touched.enabledToolkitIds)}
          required
          sx={{ ml: 3, display: 'flex' }}
          variant="standard"
        >
          <FormLabel component="legend">
            Ensembles d&apos;Outils Activés
          </FormLabel>
          <CheckboxList<ToolkitId>
            idPrefix={enabledToolkitCheckboxIdPrefix}
            items={toolkits.map((toolkit) => ({
              id: toolkit._id,
              label: toolkit.label,
            }))}
            // eslint-disable-next-line react/jsx-no-bind
            onChange={(toolkitIds) => {
              setCheckedEnabledToolkitIds(toolkitIds);
              void setFieldTouched('enabledToolkitIds', true, false);
            }}
            value={checkedEnabledToolkitIds ?? EMPTY_ARRAY}
            variant="edition"
          />
          {!!(
            getIn(errors, 'enabledToolkitIds') &&
            getIn(touched, 'enabledToolkitIds')
          ) && (
            <FormHelperText>
              {getIn(errors, 'enabledToolkitIds')}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};
